import type { MapDocument } from "../document/types.ts";
import type { FoldedZones } from "../document/zones.ts";
import { activationCategoryStyle, type NumberedActivationType } from "./activation-categories.ts";
import type { ValidationIssue, ZoneTypeDefinition } from "./adapter.ts";
import type { PresenceArea } from "./presence-area.ts";

/**
 * The Zones overlay's data (ADR-054's amendment of 2026-09-27, decisions 2 and 5): each painted type numbered
 * and styled by its place in the game's registration, the folded grid cut into connected areas of one type, as
 * the activation overlay draws an area, and the issue a type the game no longer registers raises.
 */

/**
 * One connected area of the Zones overlay: its type and the bitmap of the collision cells it covers.
 */
export interface ZoneArea {
  readonly type: string;
  readonly cells: PresenceArea;
}

/**
 * Numbers the zone types a map paints: a registered type's number is its place in the registration, 1 up, so
 * its colour is the same on every map; a type the game does not register numbers after the registered types in
 * the order it first appears, named by its id. Only the painted types are listed, in number order.
 *
 * @param registered The game's zone types in registration order.
 * @param painted The types painted, in the order they first appear.
 * @returns The painted types, numbered and styled.
 */
export function numberZoneTypes(registered: readonly ZoneTypeDefinition[], painted: readonly string[]): NumberedActivationType[] {
  const numbered: NumberedActivationType[] = [];
  const left = new Set(painted);
  registered.forEach((type, i) => {
    if (left.delete(type.id)) {
      numbered.push({ type: type.id, label: type.label, number: i + 1, style: activationCategoryStyle(i + 1) });
    }
  });
  let next = registered.length;
  for (const id of painted) {
    if (left.delete(id)) {
      next += 1;
      numbered.push({ type: id, label: id, number: next, style: activationCategoryStyle(next) });
    }
  }
  return numbered;
}

/**
 * Cuts a folded zone grid into its 4-connected areas of one type, in the order a row-major scan first meets
 * each, every area its bounding box and bitmap.
 *
 * @param folded The map's folded zones.
 * @param width The grid's width in collision cells.
 * @param height The grid's height in collision cells.
 * @returns The areas.
 * @throws RangeError when the grid is not `width` by `height`.
 */
export function zoneAreas(folded: { readonly types: FoldedZones["types"]; readonly cells: ArrayLike<number> }, width: number, height: number): ZoneArea[] {
  const size = width * height;
  if (folded.cells.length !== size) {
    throw new RangeError(`a zone grid of ${folded.cells.length} cells is not ${width} by ${height}`);
  }
  const label = new Int32Array(size).fill(-1);
  const stack = new Uint32Array(size);
  const members: number[] = [];
  const areas: ZoneArea[] = [];
  for (let start = 0; start < size; start += 1) {
    const value = folded.cells[start] as number;
    if (value === 0 || (label[start] as number) >= 0) {
      continue;
    }
    const id = areas.length;
    let top = 0;
    members.length = 0;
    label[start] = id;
    stack[top++] = start;
    let x0 = width;
    let y0 = height;
    let x1 = -1;
    let y1 = -1;
    while (top > 0) {
      const index = stack[--top] as number;
      members.push(index);
      const x = index % width;
      const y = (index - x) / width;
      x0 = Math.min(x0, x);
      y0 = Math.min(y0, y);
      x1 = Math.max(x1, x);
      y1 = Math.max(y1, y);
      for (let side = 0; side < 4; side += 1) {
        const next = side === 0 ? (x > 0 ? index - 1 : -1) : side === 1 ? (x < width - 1 ? index + 1 : -1) : side === 2 ? (y > 0 ? index - width : -1) : y < height - 1 ? index + width : -1;
        if (next >= 0 && label[next] === -1 && folded.cells[next] === value) {
          label[next] = id;
          stack[top++] = next;
        }
      }
    }
    const areaWidth = x1 - x0 + 1;
    const cells = new Uint8Array(areaWidth * (y1 - y0 + 1));
    for (const index of members) {
      const x = index % width;
      cells[((index - x) / width - y0) * areaWidth + (x - x0)] = 1;
    }
    areas.push({ type: folded.types[value - 1] as string, cells: { x0, y0, width: areaWidth, height: y1 - y0 + 1, cells } });
  }
  return areas;
}

/**
 * The issues a map's zone layers raise: one for each type a layer paints that the game does not register, which
 * the compile refuses until it is repainted.
 *
 * @param map The map.
 * @param registered The game's zone types.
 * @returns The issues, by layer then by the layer's table order.
 */
export function zoneIssues(map: MapDocument, registered: readonly ZoneTypeDefinition[]): ValidationIssue[] {
  const known = new Set(registered.map((type) => type.id));
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "zone") {
      continue;
    }
    for (const type of layer.types) {
      if (!known.has(type)) {
        issues.push({ mapId: map.id, kind: "zone.unregistered", layerId: layer.id, message: `zone layer ${layer.name} paints ${type}, a zone type the game does not register; repaint its cells with a registered type` });
      }
    }
  }
  return issues;
}
